import { ArrowDownwardOutlined, ArrowRightOutlined, ArrowUpwardOutlined, BorderAllOutlined } from "@mui/icons-material";
import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import {
	Box,
	Collapse,
	ClickAwayListener,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Paper,
	Popper,
	Typography,
	useMediaQuery
} from '@mui/material';
import SimpleBar from "simplebar-react";
import { getMenuIcon } from "../../../../../helpers/theme/getMenuIcon";
import useMenuAction from "../../../../../hooks/menu/useMenuAction";
import useMenuData from "../../../../../hooks/menu/useMenuData";
import useConfig from "../../../../../hooks/theme/useConfig";
import PermissionHOC from "../../../../../modules/permission/components/permissionHOC/permissionHOC";
import { MenuOrientation } from "../../../../../themes/config";
import Dot from "../../../../@extended/dot";
import Transitions from "../../../../@extended/transitions";

// project import
import NavItem from './navItem';

// assets

// mini-menu - wrapper
const PopperStyled = styled(Popper)(({ theme }) => ({
	overflow: 'visible',
	zIndex: 1202,
	minWidth: 210,
	'&:before': {
		content: '""',
		display: 'block',
		position: 'absolute',
		top: 38,
		left: -5,
		width: 10,
		height: 10,
		backgroundColor: theme.palette.background.paper,
		transform: 'translateY(-50%) rotate(45deg)',
		zIndex: 120,
		borderLeft: `1px solid ${theme.palette.grey.A800}`,
		borderBottom: `1px solid ${theme.palette.grey.A800}`
	}
}));

// ==============================|| NAVIGATION - LIST COLLAPSE ||============================== //

const NavCollapse = ({ menu, level, parentId, setSelectedItems, selectedItems, setSelectedLevel, selectedLevel }) => {
	const theme = useTheme();
	
	const downLG = useMediaQuery(theme.breakpoints.down('lg'));
	const { drawerOpen } = useMenuData()
	const { menuOrientation } = useConfig();
	const history = useHistory();
	const { setMenuActiveItem } = useMenuAction()
	const [open, setOpen] = useState(false);
	const [selected, setSelected] = useState(null);
	
	const [anchorEl, setAnchorEl] = useState(null);
	
	const handleClick = (event) => {
		setAnchorEl(null);
		setSelectedLevel(level);
		if (drawerOpen) {
			setOpen(!open);
			setSelected(menu.id);
			setSelectedItems(menu.id);
			if (menu.url) history.push(`${menu.url}`);
		} else {
			setAnchorEl(event?.currentTarget);
		}
	};
	
	const handlerIconLink = () => {
		if (!drawerOpen) {
			if (menu.url) history.push(`${menu.url}`);
			setSelected(menu.id);
		}
	};
	
	const handleHover = (event) => {
		setAnchorEl(event?.currentTarget);
		if (!drawerOpen) {
			setSelected(menu.id);
		}
	};
	
	const miniMenuOpened = Boolean(anchorEl);
	
	const handleClose = () => {
		setOpen(false);
		if (!miniMenuOpened) {
			if (!menu.url) {
				setSelected(null);
			}
		}
		setAnchorEl(null);
	};
	
	useMemo(() => {
		if (selected === selectedItems) {
			if (level === 1) {
				setOpen(true);
			}
		} else {
			if (level === selectedLevel) {
				setOpen(false);
				if (!miniMenuOpened && !drawerOpen && !selected) {
					setSelected(null);
				}
				if (drawerOpen) {
					setSelected(null);
				}
			}
		}
	}, [level, selected, miniMenuOpened, drawerOpen]);
	
	const { pathname } = useLocation();

	useEffect(() => {
		if (pathname === menu.url) {
			setSelected(menu.id);
		}
		// eslint-disable-next-line
	}, [pathname]);
	
	const checkOpenForParent = (child, id) => {
		child.forEach((item) => {
			if (item.url === pathname) {
				setOpen(true);
				setSelected(id);
			}
		});
	};

	useEffect(() => {
		// setOpen(false);
		if (!miniMenuOpened) {
			setSelected(null);
		}
		if (miniMenuOpened) setAnchorEl(null);
		if (menu.children) {
			menu.children.forEach((item) => {
				if (item.children?.length) {
					checkOpenForParent(item.children, menu.id);
				}
				if (item.url === pathname) {
					setSelected(menu.id);
					// setOpen(true);
				}
			});
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname, menu.children]);

	useEffect(() => {
		if (menu.url === pathname) {
			setMenuActiveItem({ openItem: [menu.id] });
			setSelected(menu.id);
			setAnchorEl(null);
			setOpen(true);
		}
	}, [pathname, menu]);
	
	const navCollapse = menu.children?.map((item) => {
		switch (item.type) {
			case 'collapse':
				return (
					<NavCollapse
						key={item.id}
						setSelectedItems={setSelectedItems}
						setSelectedLevel={setSelectedLevel}
						selectedLevel={selectedLevel}
						selectedItems={selectedItems}
						menu={item}
						level={level + 1}
						parentId={parentId}
					/>
				);
			case 'item':
				return <NavItem key={item.id} item={item} level={level + 1}/>;
			default:
				return (
					<Typography key={item.id} variant="h6" color="error" align="center">
						Fix - Collapse or Item
					</Typography>
				);
		}
	});
	
	const isSelected = selected === menu.id;
	const borderIcon = level === 1 ? <BorderAllOutlined style={{ fontSize: '1rem' }}/> : false;
	const Icon = getMenuIcon(menu.icon)
	const menuIcon = menu.icon ? <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }}/> : borderIcon;
	const textColor = 'text.contrastText';
	const popperId = miniMenuOpened ? `collapse-pop-${menu.id}` : undefined;
	const FlexBox = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' };
	console.log(selected, menu.id)
	const navigationCollapse = (
		<>
			{menuOrientation === MenuOrientation.VERTICAL || downLG ? (
				<>
					<ListItemButton
						disableRipple
						selected={isSelected}
						{...(!drawerOpen && { onMouseEnter: handleClick, onMouseLeave: handleClose })}
						onClick={(event) => {
							event.stopPropagation()
							handleClick(event)
						}}
						sx={{
							mb: 4,
							zIndex: 1201,
							borderRadius: '12px',
							marginBottom: '16px',
							background: theme.palette.secondary.main,
							...(drawerOpen && {
								display: 'flex',
								padding: '16px 48px 16px 32px',
								gap: '6px',
								maxHeight: '56px',
								'&:hover': {
									borderRadius: '12px',
									background: theme.palette.primary.dark,
									boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.20) inset',
									h6: {
										color: '#fff'
									}
								},
								'&.Mui-selected': {
									borderRadius: '12px',
									background: theme.palette.primary.dark,
									boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.20) inset',
									h6: {
										color: '#fff'
									},
									'&:hover': {
										borderRadius: '12px',
										background: theme.palette.primary.dark,
										boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.20) inset',
										
										h6: {
											color: '#fff'
										}
									}
								},
							}),
							...(!drawerOpen && {
								display: 'flex',
								padding: '10px',
								alignItems: 'center',
								justifyContent: 'center',
								maxWidth: '44px',
								'&:hover': {
									background: theme.palette.primary.dark,
								},
								'&.Mui-selected': {
									background: theme.palette.primary.dark,
									'&:hover': {
										background: theme.palette.primary.dark,
									}
								}
							})
						}}
					>
						<ListItemIcon
							sx={{
								minWidth: 24,
								color: '#FFFFFF'
							}}
						>
							{menuIcon}
						</ListItemIcon>
						{(drawerOpen || (!drawerOpen && level !== 1)) && (
							<ListItemText
								primary={
									<Typography
										variant="body1"
										sx={{
											color: isSelected ? textColor : textColor,
											textWrap: 'wrap',
										}}
									>
										{menu.title}
									</Typography>
								}
							/>
						)}
						{(drawerOpen || (!drawerOpen && level !== 1)) &&
							(miniMenuOpened || open ? (
								<ArrowUpwardOutlined
									style={{ fontSize: '0.625rem', marginLeft: 1, color: '#FFFFFF' }}/>
							) : (
								<ArrowDownwardOutlined
									style={{ fontSize: '0.625rem', marginLeft: 1, color: '#FFFFFF' }}/>
							))}
						
						{!drawerOpen && (
							<PopperStyled
								open={miniMenuOpened}
								anchorEl={anchorEl}
								placement="right-start"
								style={{
									zIndex: 2001
								}}
								popperOptions={{
									modifiers: [
										{
											name: 'offset',
											options: {
												offset: [-12, 1]
											}
										}
									]
								}}
							>
								{({ TransitionProps }) => (
									<Transitions in={miniMenuOpened} {...TransitionProps}>
										<Paper
											sx={{
												overflow: 'hidden',
												mt: 1.5,
												boxShadow: theme.customShadows.z1,
												backgroundImage: 'none',
												border: `1px solid ${theme.palette.divider}`,
												p: 1,
											}}
										>
											<ClickAwayListener onClickAway={handleClose}>
													<SimpleBar
														sx={{
															overflowX: 'hidden',
															overflowY: 'auto',
															// maxHeight: 'calc(100vh - 170px)',
														}}
													>
														{navCollapse}
													</SimpleBar>
											</ClickAwayListener>
										</Paper>
									</Transitions>
								)}
							</PopperStyled>
						)}
					</ListItemButton>
					{drawerOpen && (
						<Collapse in={open} timeout="auto" unmountOnExit>
							<List sx={{ p: 0 }}>{navCollapse}</List>
						</Collapse>
					)}
				</>
			) : (
				<>
					<ListItemButton
						id={`boundary-${popperId}`}
						disableRipple
						selected={isSelected}
						onMouseEnter={handleHover}
						onMouseLeave={handleClose}
						onClick={handleHover}
						aria-describedby={popperId}
						sx={{
							'&.Mui-selected': {
								bgcolor: 'transparent'
							}
						}}
					>
						<Box onClick={handlerIconLink} sx={FlexBox}>
							{menuIcon && level === 1 && (
								<ListItemIcon
									sx={{
										minWidth: 24,
										color: '#FFFFFF'
									}}
								>
									{menuIcon}
								</ListItemIcon>
							)}
							{!menuIcon && level !== 1 && (
								<ListItemIcon
									sx={{
										minWidth: 24,
										color: '#FFFFFF'
									}}
								>
									<Dot size={4} color={isSelected ? 'primary' : 'secondary'}/>
								</ListItemIcon>
							)}
							<ListItemText
								primary={
									<Typography variant="body1" color="inherit" sx={{ my: 'auto' }}>
										{menu.title}
									</Typography>
								}
							/>
							{miniMenuOpened ? <ArrowRightOutlined/> : <ArrowDownwardOutlined/>}
						</Box>
						{anchorEl && (
							<PopperStyled
								id={popperId}
								open={miniMenuOpened}
								anchorEl={anchorEl}
								placement="right-start"
								style={{
									zIndex: 2001
								}}
								modifiers={[
									{
										name: 'offset',
										options: {
											offset: [-10, 0]
										}
									}
								]}
							>
								{({ TransitionProps }) => (
									<Transitions in={miniMenuOpened} {...TransitionProps}>
										<Paper
											sx={{
												overflow: 'hidden',
												mt: 1.5,
												py: 0.5,
												boxShadow: theme.shadows[8],
												backgroundImage: 'none'
											}}
										>
											<ClickAwayListener onClickAway={handleClose}>
												<SimpleBar
													sx={{
														overflowX: 'hidden',
														overflowY: 'auto',
														maxHeight: 'calc(100vh - 170px)'
													}}
												>
													{navCollapse}
												</SimpleBar>
											</ClickAwayListener>
										</Paper>
									</Transitions>
								)}
							</PopperStyled>
						)}
					</ListItemButton>
				</>
			)}
		</>
	)
	
	return menu.permissions && menu.permissions.length > 0 ? (
		<PermissionHOC
			requiredPermissions={[...menu.permissions]}>
			{navigationCollapse}
		</PermissionHOC>
	) : (
		<>{navigationCollapse}</>
	)
};
//
// NavCollapse.propTypes = {
//     menu: PropTypes.object,
//     level: PropTypes.number,
//     parentId: PropTypes.string,
//     setSelectedItems: PropTypes.func,
//     selectedItems: PropTypes.string,
//     setSelectedLevel: PropTypes.func,
//     selectedLevel: PropTypes.number
// };

export default NavCollapse;
